/**
 * Pre-flight check runner (SD-13, STAGE-DESIGN §4). Runs on Performance Mode entry
 * and on demand (Settings → Pre-flight). Each row reads its numeric bound
 * from the user-adjustable thresholds (preflightConfig.ts), so the same check
 * reads stricter at a tight club and looser on a rough hotspot.
 *
 * Rows that need an OPBridge surface that isn't shipped yet (lock drift,
 * host-side MIDI-port enumeration) degrade to an honest INFO/WARN rather than
 * a false PASS - "not built yet", never a green tick. Such a row must SAY it
 * is a placeholder: the 2026-09-03 live-risk audit found "Docs in sync"
 * pushing `info: sync not configured (OPBridge doc store pending)`
 * unconditionally, months after the doc store shipped, and while the very
 * signals it needed were already being rendered in Settings. An unconditional
 * `rows.push` is furniture on a checklist the operator reads before walking on
 * stage; if a row cannot report a problem, its text must make that obvious.
 *
 * Impure only at the edges (network + store reads); returns a plain result
 * the panel renders.
 */

import { primaryBridge, bridges, getConnection } from '../opbridge/store.ts';
import { OpbridgeError } from '../opbridge/httpClient.ts';
import { activeProject, projectsSaveFailedAt } from '../layout/projectStore.ts';
import { timelines } from '../timeline/store.ts';
import { refreshKnownPorts } from '../timeline/bridgeSync.ts';
import { lintProject } from './lint.ts';
import { matrixCcLabel } from '../matrix/store.ts';
import { wakeLockMode } from './wakeLock.ts';
import { thresholds } from './preflightConfig.ts';
import { persistState, readStorageUsage } from '../storage/persist.ts';
import {
  projectSyncState,
  projectSyncLastError,
  projectSyncLastSyncedAt,
} from '../layout/projectSync.ts';

/** Docs-sync staleness bounds. DERIVED, not picked: projectSync.ts polls every
 *  POLL_MS = 15_000ms and advances `lastSyncedAt` on every clean tick, push or
 *  no push. 60s is four missed ticks; 180s is twelve. Wide enough that one slow
 *  tick or a brief connection flap cannot fire them, because a checklist row
 *  that cries wolf trains the operator to skim the whole list. */
const DOCS_STALE_WARN_MS = 60_000;
const DOCS_STALE_FAIL_MS = 180_000;

/** Warn when a bridge's cached WebTransport pin set has under this long to
 *  live. DERIVED from the daemon's own rotation: WebKit caps a pinned cert at
 *  14 days and OPBridge re-mints ~3 days before expiry (wtTransport.ts), so
 *  48h is comfortably inside the window where reconnecting once while online
 *  will pick up a fresh pin, and outside the normal churn. */
const WT_PIN_WARN_S = 48 * 3600;

/** The real horizon for a bridge's offline path: the LAST pin in the published
 *  chain, falling back to the current cert's own expiry on a daemon too old to
 *  publish one. Mirrors `wtKey()` in opbridge/store.ts deliberately - if these
 *  two ever disagree, the pre-flight would green-light a path the dialler
 *  refuses to use. */
function wtPinHorizon(b: { wt_hashes_valid_until_unix_s?: number | null; wt_expires_at_unix_s?: number | null }): number | null {
  return b.wt_hashes_valid_until_unix_s ?? b.wt_expires_at_unix_s ?? null;
}

export type CheckStatus = 'pass' | 'warn' | 'fail' | 'info';

export interface CheckRow {
  id: string;
  label: string;
  status: CheckStatus;
  detail: string;
  /** One-line fix hint shown on tap. */
  remedy?: string | undefined;
}

export interface PreflightResult {
  rows: CheckRow[];
  ranAtMs: number;
  /** Worst status across all rows — drives the header chip color. */
  worst: CheckStatus;
}

const SEVERITY: Record<CheckStatus, number> = { pass: 0, info: 1, warn: 2, fail: 3 };

function worstOf(rows: CheckRow[]): CheckStatus {
  let worst: CheckStatus = 'pass';
  for (const r of rows) if (SEVERITY[r.status] > SEVERITY[worst]) worst = r.status;
  return worst;
}

/** Run the full pre-flight. Never throws — a check that errors becomes a
 *  FAIL row with the error text. */
export async function runPreflight(): Promise<PreflightResult> {
  const rows: CheckRow[] = [];
  const cfg = thresholds();
  const primary = primaryBridge();
  const conn = primary ? getConnection(primary.id) : null;

  // ── 1. EVERY paired bridge reachable + identity ──
  //
  // Was a single "Primary reachable" row. That was a real gig hazard after
  // DEC-169 (CP-094) retired the primary-only dispatch model: items now carry
  // `bridgeIds` and fan out to ANY paired bridge, so checking one bridge could
  // report all-green while the bridge an item actually fires to was dead. The
  // Mac being down while the dev PC happened to be default was a passing
  // pre-flight and a broken rig. One row per bridge, named, so a failure says
  // WHICH machine. (The word "primary" is also stale vocabulary - DEC-169
  // replaced it with "default".)
  const paired = bridges();
  if (paired.length === 0) {
    rows.push({ id: 'reachable', label: 'OPBridge reachable', status: 'fail',
      detail: 'no OPBridge paired', remedy: 'Pair one in Connect.' });
  } else {
    const checks = await Promise.all(paired.map(async (b): Promise<CheckRow> => {
      const label = `${b.name} reachable`;
      const id = `reachable:${b.id}`;
      const c = getConnection(b.id);
      if (!c) {
        return { id, label, status: 'fail', detail: 'not connected',
          remedy: 'Open Connect and reconnect this bridge.' };
      }
      try {
        const h = await c.httpClient().health();
        const seen = h.instance_name ?? '';
        const want = b.bridge_instance_name ?? '';
        if (want && seen && seen !== want) {
          return { id, label, status: 'warn',
            detail: `reached ${seen}, expected ${want}`,
            remedy: 'Wrong network? Check you are on the gig WiFi.' };
        }
        return { id, label, status: 'pass', detail: h.machine_name ?? seen ?? b.name };
      } catch (err) {
        return { id, label, status: 'fail', detail: errText(err),
          remedy: 'Bridge down or wrong network.' };
      }
    }));
    rows.push(...checks);
  }

  // ── 1b. Offline resilience — which bridges need the internet to be FOUND ──
  //
  // A bridge reached via `<hex>.bridge.overpoweredapps.net` resolves through
  // PUBLIC DNS with a 120s TTL. At a venue with no internet the name stops
  // resolving about two minutes in and that bridge disappears mid-set, even
  // though it is sitting on the same LAN. Reachable-right-now says nothing
  // about that, because right now there IS internet - so this is a separate
  // row on purpose. See OFFLINE-LAN-RESEARCH.md.
  if (paired.length > 0) {
    // WT-AWARE SINCE 2026-09-03, and this row was warning on a HEALTHY rig
    // before that. It classified on `baseUrl` alone, so a bridge whose offline
    // path was fully armed still read WARN and still recommended "point the
    // gig network at OPBridge for DNS" - the remedy from the world before
    // DEC-195 solved this. A check that fires on a healthy system is worse
    // than no check: it trains the operator to skim, and then the real rows
    // get skimmed too.
    //
    // A bridge is only DNS-bound if it needs a public lookup AND has no live
    // pin to dial the LAN IP with. `wtPinHorizon` is the same expiry the
    // dialler uses, so this cannot green-light a path `wtKey()` would refuse.
    const nowS = Date.now() / 1000;
    const hasLivePin = (b: typeof paired[number]) => {
      const h = wtPinHorizon(b);
      return !!b.wt_url && h !== null && h > nowS;
    };
    const dnsBound = paired.filter((b) => needsPublicDns(b.baseUrl) && !hasLivePin(b));
    if (dnsBound.length === 0) {
      const pinned = paired.filter(hasLivePin).length;
      rows.push({ id: 'offline-dep', label: 'Offline resilience', status: 'pass',
        detail: pinned > 0
          ? `no bridge depends on internet DNS (${pinned} via pinned WebTransport)`
          : 'no bridge depends on internet DNS' });
    } else {
      rows.push({ id: 'offline-dep', label: 'Offline resilience', status: 'warn',
        detail: `${dnsBound.length} of ${paired.length} need internet DNS (${dnsBound.map((b) => b.name).join(', ')})`,
        remedy: 'Without internet these drop ~2 min in. Point the gig network at OPBridge for DNS.' });
    }
  }

  // ── 1c. Storage persistence (2026-08-31) ──
  //
  // Safari evicts script-writable storage after ~7 days unused. Evicted =
  // opening the app at a venue to a blank shell: no cached UI, no layouts, no
  // pairings. A granted origin is exempt, and WebKit DOES grant it to an
  // installed app (measured on iPadOS 26.6.1). This row exists because that
  // grant can be absent or revoked, and the failure is slow and silent - you
  // find out at the gig, which is exactly the class this pre-flight is for.
  {
    const p = persistState();
    const used = await readStorageUsage();
    const usedText = used === null ? '' : `; OPButtons uses ${used < 1048576 ? `${Math.ceil(used / 1024)} KB` : `${(used / 1048576).toFixed(1)} MB`} here`;
    if (p === 'persisted') {
      rows.push({ id: 'storage', label: 'Storage persistent', status: 'pass',
        detail: `exempt from Safari's 7-day clean-up${usedText}` });
    } else if (p === 'denied') {
      rows.push({ id: 'storage', label: 'Storage persistent', status: 'warn',
        detail: `DENIED - storage can be evicted after ~7 days unused${usedText}`,
        remedy: 'Add OPButtons to the Home Screen and open it from there; a browser tab is often denied.' });
    } else if (p === 'unsupported') {
      rows.push({ id: 'storage', label: 'Storage persistent', status: 'info',
        detail: `no Storage API on this browser${usedText}` });
    } else {
      rows.push({ id: 'storage', label: 'Storage persistent', status: 'info',
        detail: `not determined yet${usedText}` });
    }
    // 2026-10-18: a full iPad said so, then came up empty - persistence did
    // not save it. A failing save is the warning we can see beforehand.
    if (projectsSaveFailedAt() !== null) {
      rows.push({ id: 'storage-save', label: 'Saving on this device', status: 'fail',
        detail: 'storage is full - changes are not being kept here',
        remedy: 'Free up space on the device now. A full iPad can wipe the app; OPBridge keeps a copy while connected.' });
    }
  }

  // ── 2. Latency (N× /health, p95) ──
  if (conn) {
    const samples: number[] = [];
    const n = Math.max(3, Math.round(cfg.latencySamples));
    for (let i = 0; i < n; i++) {
      const t0 = performance.now();
      try { await conn.httpClient().health(); samples.push(performance.now() - t0); }
      catch { /* a dropped sample counts as nothing; a fully-down bridge already failed row 1 */ }
    }
    if (samples.length === 0) {
      rows.push({ id: 'latency', label: 'Latency', status: 'fail', detail: 'no samples returned' });
    } else {
      const p95 = percentile(samples, 95);
      const status: CheckStatus = p95 >= cfg.rttFailMs ? 'fail' : p95 >= cfg.rttWarnMs ? 'warn' : 'pass';
      rows.push({ id: 'latency', label: 'Latency', status,
        detail: `p95 ${p95.toFixed(0)}ms (${samples.length}/${n} · warn ${cfg.rttWarnMs} / fail ${cfg.rttFailMs})`,
        remedy: status === 'pass' ? undefined : 'Venue WiFi trouble - move closer to the router or adjust thresholds.' });
    }
  }

  // ── 3 + 5. Bearer valid + Clock alive — one authed GET /clock/status feeds
  // both rows (a 401 = bad bearer; the snapshot = clock freshness). One call,
  // not two — matters on the slow-auth build where each authed request is ~8s.
  if (conn) {
    try {
      const c = await conn.httpClient().clockStatus();
      rows.push({ id: 'bearer', label: 'Bearer valid', status: 'pass', detail: 'authed OK' });
      if (!c.has_ever_received) {
        rows.push({ id: 'clock', label: 'Clock alive', status: 'warn',
          detail: 'no transport received yet', remedy: 'Start Ableton / the OP-BridgeSync device.' });
      } else if (c.stale || c.age_ms >= cfg.clockStaleFailMs) {
        rows.push({ id: 'clock', label: 'Clock alive', status: 'warn',
          detail: `transport silent (${c.age_ms}ms)`, remedy: 'Ableton stopped or the sync device is closed.' });
      } else {
        rows.push({ id: 'clock', label: 'Clock alive', status: 'pass', detail: `${c.source} ${c.tempo.toFixed(0)}bpm` });
      }
    } catch (err) {
      if (err instanceof OpbridgeError && err.kind === 'unauthorized') {
        rows.push({ id: 'bearer', label: 'Bearer valid', status: 'fail',
          detail: 'server returned 401', remedy: 'Access revoked - re-pair in Connect.' });
        rows.push({ id: 'clock', label: 'Clock alive', status: 'warn', detail: 'bearer invalid - clock unchecked' });
      } else {
        // 503 (no clock wired) / network — the request reached auth, so the bearer is fine.
        rows.push({ id: 'bearer', label: 'Bearer valid', status: 'pass', detail: 'authed (no 401)' });
        rows.push({ id: 'clock', label: 'Clock alive', status: 'warn', detail: errText(err) });
      }
    }
  }

  // ── 4. MIDI output, PER BRIDGE: set, and present on that computer ──
  //
  // Was one "MIDI output set" row for the primary that only checked the
  // field was filled. Two holes, both gig hazards: every bridge sends MIDI
  // (DEC-169, same reasoning as row 1), and a filled-in port can still be
  // missing - DEFAULT_MIDI_PORT fills every new row, so a fresh Windows
  // pairing inherited `OPBridge-Ableton`, a port that platform does not have
  // (measured 2026-09-01). Now each connected bridge's `/midi/ports` is read
  // (one call each, cached 15 s, the SAME list and rule the timeline compile
  // warns with: outputs, inputs and OPBridge's own built-in names count as
  // present). A bridge that is not connected gets no row here: its
  // "reachable" row above already fails.
  {
    const midiRows = await Promise.all(paired.map(async (b): Promise<CheckRow | null> => {
      const id = `midi:${b.id}`;
      const label = `${b.name} MIDI out`;
      const port = b.defaultMidiOutput;
      if (!port) {
        return { id, label, status: 'fail', detail: 'MIDI output port cleared on this bridge',
          remedy: 'Open Connect and pick a MIDI out port for this computer.' };
      }
      const c = getConnection(b.id);
      if (!c || c.status() !== 'connected') return null;
      const names = await refreshKnownPorts(b);
      if (!names) {
        return { id, label, status: 'warn', detail: `${port} (could not read this computer's ports)`,
          remedy: 'Check the bridge, then run pre-flight again.' };
      }
      if (names.has(port)) return { id, label, status: 'pass', detail: port };
      return { id, label, status: 'fail', detail: `"${port}" is not a MIDI port on this computer`,
        remedy: 'Open Connect and pick a MIDI out port this computer has.' };
    }));
    for (const r of midiRows) if (r) rows.push(r);
  }

  // ---- 6. Docs in sync (LIVE since 2026-09-03) ----
  //
  // Reads the same three signals `SettingsPanel` already renders. It used to
  // push an unconditional `info: sync not configured (OPBridge doc store
  // pending)` - false since the doc store shipped, and structurally unable to
  // report a real sync failure on the night it mattered.
  //
  // AGE, NOT STATE, IS THE LOAD-BEARING HALF, and SettingsPanel had already
  // learned why: `projectSyncState` reverts to 'idle' after a successful save,
  // so a state-only readout shows a reassuring "up to date" for a sync that has
  // not completed in hours. `lastSyncedAt` advances on every clean tick, push
  // or no push, so its AGE is what catches a silent stall.
  {
    const err = projectSyncLastError();
    const at = projectSyncLastSyncedAt();
    if (err) {
      rows.push({ id: 'docs', label: 'Docs in sync', status: 'fail',
        detail: `sync error: ${err}`,
        remedy: 'Check your default OPBridge is connected, then look at Settings > Diagnostics > Project sync.' });
    } else if (at === null) {
      rows.push({ id: 'docs', label: 'Docs in sync', status: 'warn',
        detail: projectSyncState() === 'idle' ? 'never synced on this device' : 'starting up',
        remedy: 'Needs a connected primary OPBridge. Layout edits stay local-only until it syncs.' });
    } else {
      const ageMs = Date.now() - at;
      const ageS = Math.round(ageMs / 1000);
      if (ageMs >= DOCS_STALE_FAIL_MS) {
        rows.push({ id: 'docs', label: 'Docs in sync', status: 'fail',
          detail: `last clean sync ${ageS}s ago (expected every ~15s)`,
          remedy: 'Sync has stalled. Reconnect the primary OPBridge before trusting anything edited on another device.' });
      } else if (ageMs >= DOCS_STALE_WARN_MS) {
        rows.push({ id: 'docs', label: 'Docs in sync', status: 'warn',
          detail: `last clean sync ${ageS}s ago (expected every ~15s)` });
      } else {
        rows.push({ id: 'docs', label: 'Docs in sync', status: 'pass',
          detail: `synced ${ageS}s ago` });
      }
    }
  }

  // ---- 7. Lock drift (NOT BUILT - a placeholder, and it says so) ----
  //
  // Genuinely unimplemented; lock-for-tonight is v2. Kept on the list so the
  // gap stays visible, but worded as a placeholder rather than as a check that
  // ran. Compare row 6 above, whose near-identical phrasing read as a RESULT
  // for months while checking nothing.
  rows.push({ id: 'lock', label: 'Lock drift', status: 'info',
    detail: 'NOT BUILT - lock-for-tonight is a later feature; nothing is checked here' });

  // ---- 7b. Offline path armed (2026-09-03) ----
  //
  // WebTransport with pinned certs is the PREFERRED transport since CP-117 /
  // DEC-195, and it is what makes a venue with no internet survivable: it
  // dials a raw LAN IP, so no DNS, no CA install, no router config.
  //
  // WHY THIS ROW EXISTS. The pins expire. WebKit caps a pinned cert at 14 days
  // and the daemon re-mints ~3 days before expiry, so a cached pin dies around
  // day 10 (wtTransport.ts). SettingsPanel's "Offline path" readout already
  // surfaces the same horizon on demand (since 2026-08-31) - this row is the
  // PROACTIVE half of that: Settings is somewhere you have to go and look,
  // pre-flight is what runs before a set without being asked. An iPad that sat
  // in a case for a fortnight otherwise reaches the venue with a dead offline
  // path and nothing volunteers it. Under-warning here costs the set.
  if (paired.length > 0) {
    const nowS = Date.now() / 1000;
    const unarmed = paired.filter((b) => !b.wt_url || wtPinHorizon(b) === null);
    const dead = paired.filter((b) => {
      const h = wtPinHorizon(b);
      return !!b.wt_url && h !== null && h <= nowS;
    });
    const expiring = paired.filter((b) => {
      const h = wtPinHorizon(b);
      return h !== null && h > nowS && h - nowS < WT_PIN_WARN_S;
    });
    if (unarmed.length === paired.length) {
      rows.push({ id: 'offline-path', label: 'Offline path armed', status: 'warn',
        detail: 'no bridge has a WebTransport pin cached',
        remedy: 'Connect to each bridge once while online; the pin is learned from /health and cached for the venue.' });
    } else if (unarmed.length > 0) {
      rows.push({ id: 'offline-path', label: 'Offline path armed', status: 'warn',
        detail: `${paired.length - unarmed.length}/${paired.length} armed (missing: ${unarmed.map((b) => b.name).join(', ')})`,
        remedy: 'Connect to the missing bridge once while online to cache its pin.' });
    } else if (dead.length > 0) {
      // ALREADY DEAD is a different and worse state than EXPIRING, and must not
      // be rounded into it. `wtKey()` refuses to dial a lapsed pin at all, so
      // this bridge has NO offline path right now - at a venue it is simply
      // gone. The first cut of this row reported it as "expires in 0h", which
      // reads like a deadline approaching rather than one already missed.
      rows.push({ id: 'offline-path', label: 'Offline path armed', status: 'fail',
        detail: `pin EXPIRED on ${dead.map((b) => b.name).join(', ')} - no offline path`,
        remedy: 'Connect to that bridge while online to mint a fresh pin. Until then it needs internet DNS to be found at all.' });
    } else if (expiring.length > 0) {
      const soonestS = Math.min(...expiring.map((b) => (wtPinHorizon(b) ?? 0) - nowS));
      rows.push({ id: 'offline-path', label: 'Offline path armed', status: 'warn',
        detail: `pin expires in ${Math.max(0, Math.round(soonestS / 3600))}h on ${expiring.map((b) => b.name).join(', ')}`,
        remedy: 'Open the app while the bridge is reachable before the set, to refresh the pin.' });
    } else {
      rows.push({ id: 'offline-path', label: 'Offline path armed', status: 'pass',
        detail: `${paired.length}/${paired.length} bridge${paired.length === 1 ? '' : 's'} pinned` });
    }
  }

  // ── 8. Layout lint clean (SD-14) ──
  const project = activeProject();
  if (project) {
    const timelineIds = new Set(timelines().map((t) => t.id));
    const findings = lintProject(project, { timelineIds, matrixLabel: matrixCcLabel });
    if (findings.length === 0) {
      rows.push({ id: 'lint', label: 'Layout lint', status: 'pass', detail: 'clean' });
    } else {
      const hasError = findings.some((f) => f.severity === 'error');
      rows.push({ id: 'lint', label: 'Layout lint', status: hasError ? 'fail' : 'warn',
        detail: `${findings.length} issue${findings.length === 1 ? '' : 's'} - ${findings[0]!.message}`,
        remedy: 'Open the layout and resolve duplicate CCs / off-canvas items.' });
    }
  }

  // ── 9. Wake lock armed (SD-3) ──
  {
    const m = wakeLockMode();
    if (m === 'api' || m === 'video') {
      rows.push({ id: 'wake', label: 'Wake lock', status: 'pass', detail: m === 'api' ? 'screen lock held' : 'video keep-awake' });
    } else if (m === 'unavailable') {
      rows.push({ id: 'wake', label: 'Wake lock', status: 'warn',
        detail: 'unavailable', remedy: 'Set iPad Auto-Lock: Never for the gig.' });
    } else {
      rows.push({ id: 'wake', label: 'Wake lock', status: 'info', detail: 'arms on Performance Mode entry' });
    }
  }

  // ── 10. Battery (not on iPad Safari) ──
  {
    const getBattery = (navigator as unknown as { getBattery?: () => Promise<{ level: number }> }).getBattery;
    if (typeof getBattery === 'function') {
      try {
        const b = await getBattery.call(navigator);
        const pct = Math.round(b.level * 100);
        rows.push({ id: 'battery', label: 'Battery', status: pct <= cfg.batteryWarnPct ? 'warn' : 'pass',
          detail: `${pct}%`, remedy: pct <= cfg.batteryWarnPct ? 'Plug in before the set.' : undefined });
      } catch {
        rows.push({ id: 'battery', label: 'Battery', status: 'info', detail: 'check manually' });
      }
    } else {
      rows.push({ id: 'battery', label: 'Battery', status: 'info', detail: 'check manually (no API on iPad)' });
    }
  }

  // ── 11. Panic reachable (v0 path: /midi/send + a configured port) ──
  {
    const bearerOk = rows.find((r) => r.id === 'bearer')?.status !== 'fail';
    // A port that is filled in but missing on that computer (row 4) cannot
    // carry the v0 panic either.
    const primaryMidi = primary ? rows.find((r) => r.id === `midi:${primary.id}`) : undefined;
    const portOk = !!primary?.defaultMidiOutput && primaryMidi?.status !== 'fail';
    if (bearerOk && portOk) {
      rows.push({ id: 'panic', label: 'Panic reachable', status: 'pass', detail: 'v0 (PWA CC 120/121/123)' });
    } else {
      rows.push({ id: 'panic', label: 'Panic reachable', status: 'warn',
        detail: !portOk ? 'no MIDI output' : 'bearer invalid',
        remedy: 'Panic needs a valid bearer + a default output port.' });
    }
  }

  return { rows, ranAtMs: Date.now(), worst: worstOf(rows) };
}

/** True when reaching this base URL requires a PUBLIC DNS lookup - i.e. it
 *  stops working at a venue with no internet, roughly one TTL after the
 *  uplink dies. Offline-safe hosts are IP literals (no lookup at all) and
 *  `.local` names (mDNS, link-local, no internet involved).
 *
 *  Deliberately errs toward "needs DNS" on anything unparseable: over-warning
 *  costs a glance, under-warning costs a set. Exported for direct testing -
 *  a check nobody can point at a known-bad input is not a check. */
export function needsPublicDns(baseUrl: string): boolean {
  let host: string;
  try {
    host = new URL(baseUrl).hostname;
  } catch {
    return true;
  }
  if (!host) return true;
  // IPv6 literals arrive bracket-stripped from `URL.hostname`.
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return false;
  if (host.includes(':')) return false;
  const lower = host.toLowerCase();
  if (lower === 'localhost' || lower.endsWith('.localhost')) return false;
  if (lower.endsWith('.local')) return false;
  return true;
}

function percentile(values: number[], p: number): number {
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.ceil((p / 100) * sorted.length) - 1);
  return sorted[Math.max(0, idx)]!;
}

function errText(err: unknown): string {
  return err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
       : err instanceof Error ? err.message
       : String(err);
}
